import { cast, flow, Instance, types } from 'mobx-state-tree';
import { StudentResultSheet } from '@models/StudentResultSheet';
import { getStudentResultSheetData } from '@modules/resultSheet';
import { apolloClient } from '@lib/apollo';
import {
  batchSubjectConfigurationForResultSheetQuery,
  RESULT_SHEET,
  result_sheetQuery,
  result_sheetQueryVariables,
  STUDENTS_FOR_RESULT_SHEET,
  studentsForResultSheetQuery,
  studentsForResultSheetQueryVariables,
} from '@app-types/graphql-types';
import { AddToast } from 'react-toast-notifications';
import { Dispatch, SetStateAction } from 'react';
import { logError } from '@lib/logger';

interface LoadDataArgs {
  batchClassId: string;
  selectedSubject: batchSubjectConfigurationForResultSheetQuery['batch_subject_configuration'][0];
  addToast: AddToast;
  setIsDataLoading: Dispatch<SetStateAction<boolean>>;
  resultSheetId?: string;
  userId: string;
}

export const ResultSheet = types
  .model('ResultSheetModel', {
    studentResultSheets: types.optional(types.array(StudentResultSheet), []),
    status: types.optional(types.string, 'draft'),
    semesterId: types.maybe(types.string),
    batchClassId: types.maybe(types.string),
    preparedByFirstName: types.maybe(types.string),
    preparedByLastName: types.maybe(types.string),
    isOwnResultSheet: types.optional(types.boolean, true),
    academicHeadRemark: types.maybe(types.string),
  })
  .actions((self) => ({
    clearState() {
      self.studentResultSheets = cast([]);
      self.status = 'draft';
      self.semesterId = undefined;
      self.batchClassId = undefined;
      self.academicHeadRemark = undefined;
    },
    loadData: flow(function* loadDataAsync({
      batchClassId,
      selectedSubject,
      addToast,
      setIsDataLoading,
      resultSheetId,
      userId,
    }: LoadDataArgs) {
      if (resultSheetId ? self.studentResultSheets.length === 0 : true) {
        try {
          self.studentResultSheets = cast([]);
          self.status = 'draft';
          self.batchClassId = undefined;
          self.semesterId = undefined;
          self.academicHeadRemark = undefined;

          setIsDataLoading(true);

          let resultSheetVariables: result_sheetQueryVariables;

          if (resultSheetId) {
            resultSheetVariables = {
              where: {
                id: {
                  _eq: resultSheetId,
                },
              },
            };
          } else {
            resultSheetVariables = {
              where: {
                batchClassId: {
                  _eq: batchClassId,
                },
                batchSubjectConfigurationId: {
                  _eq: selectedSubject?.id,
                },
              },
            };
          }

          const resultSheetData = yield apolloClient.query<result_sheetQuery, result_sheetQueryVariables>({
            query: RESULT_SHEET,
            fetchPolicy: 'no-cache',
            variables: resultSheetVariables,
          });

          // Set semester, prepared by and batchClassId if resultSheetId
          if (resultSheetData?.data?.result_sheet[0]) {
            const selectedSemester = resultSheetData.data?.result_sheet[0]?.batch_subject_configuration?.subject?.course_semester_subjects_subjects.find(
              (courseSemester) =>
                courseSemester.course_semester.courseId ===
                resultSheetData.data?.result_sheet[0]?.batch_subject_configuration?.batch?.courseId,
            );

            if (selectedSemester) {
              self.semesterId = selectedSemester.course_semester.id;
            }

            self.batchClassId = resultSheetData.data?.result_sheet[0]?.batchClassId;
            self.preparedByFirstName = resultSheetData.data?.result_sheet[0].user_public.firstName;
            self.preparedByLastName = resultSheetData.data?.result_sheet[0].user_public.lastName;
            self.isOwnResultSheet = resultSheetData.data?.result_sheet[0].user_public.id === userId;
            self.academicHeadRemark = resultSheetData.data?.result_sheet[0].academicHeadRemark || undefined;
          }

          let studentResultDataVariables: studentsForResultSheetQueryVariables;

          if (resultSheetId) {
            studentResultDataVariables = {
              where: {
                batchClassId: resultSheetData.data.result_sheet[0].batchClassId,
                batchSubjectConfigurationId: resultSheetData.data.result_sheet[0].batchSubjectConfigurationId,
              },
            };
          } else {
            studentResultDataVariables = {
              where: {
                batchClassId,
                batchSubjectConfigurationId: selectedSubject.id,
              },
            };
          }

          const studentResultData = yield apolloClient.query<
            studentsForResultSheetQuery,
            studentsForResultSheetQueryVariables
          >({
            query: STUDENTS_FOR_RESULT_SHEET,
            fetchPolicy: 'no-cache',
            variables: studentResultDataVariables,
          });

          const studentResultSheets = getStudentResultSheetData({
            selectedSubject,
            studentsWithAttendance: studentResultData.data.studentsWithAttendance,
          });

          if (!resultSheetData?.data?.result_sheet || resultSheetData?.data?.result_sheet.length === 0) {
            self.studentResultSheets = cast(studentResultSheets);
          } else {
            const studentResultSheetData = studentResultSheets.map((studentResultSheet) => {
              const studentResultSheetSavedData = resultSheetData?.data?.result_sheet[0].student_result_sheets.find(
                (sheet) => sheet.studentId === studentResultSheet.id,
              );

              return {
                ...studentResultSheet,
                remarks: studentResultSheetSavedData?.remark || '',
                customMarks: studentResultSheetSavedData?.customMarks || undefined,
                studentResults: studentResultSheet?.studentResults?.map((studentResult) => {
                  const studentResultSavedData = studentResultSheetSavedData?.student_results.find(
                    (studentResultDataItem) =>
                      studentResultDataItem.batchSubjectExtraSlotId === studentResult.batchSubjectExtraSlotId ||
                      studentResultDataItem.assessmentId === studentResult.assessmentId,
                  );

                  if (studentResultSavedData) {
                    return {
                      ...studentResult,
                      firstAttemptMarks: typeof studentResultSavedData?.firstAttemptMarks === 'number' ? studentResultSavedData?.firstAttemptMarks : undefined,
                      isFirstAttemptNYC: studentResultSavedData?.isFirstAttemptNYC || undefined,
                      secondAttemptMarks: typeof studentResultSavedData?.secondAttemptMarks === 'number' ? studentResultSavedData?.secondAttemptMarks : undefined,
                      isSecondAttemptNYC: studentResultSavedData?.isSecondAttemptNYC || undefined,
                      thirdAttemptMarks: typeof studentResultSavedData?.thirdAttemptMarks === 'number' ? studentResultSavedData?.thirdAttemptMarks : undefined,
                      isThirdAttemptNYC: studentResultSavedData?.isThirdAttemptNYC || undefined,
                    };
                  }
                  return studentResult;
                }),
              };
            });

            self.studentResultSheets = cast(studentResultSheetData);
            self.status = resultSheetData.data.result_sheet[0].status;
          }
        } catch (error) {
          addToast('Error. Something went wrong. Please try again.', {
            appearance: 'error',
            autoDismiss: true,
          });

          logError(error);
        } finally {
          setIsDataLoading(false);
        }
      }
    }),
  }));

export type ResultSheetModelType = Instance<typeof ResultSheet>;
